import { useEffect, useState, type ReactNode } from "react"
import { Link, useNavigate } from "react-router-dom"
import { api } from "../../lib/api"
import type { Compra, GatewayDePagamento, Produto, QuestaoRevisada, Resultado, ResultadoUnidade } from "../../types"
import { nota as formatarNota, parcelado, prazo, reais } from "../../lib/format"
import { listar } from "../../lib/texto"
import {
  beneficioDoPrazo,
  chamadaFinal,
  mancheteDaOferta,
  mancheteDoAprovado,
  objecaoDoCusto,
  objecaoDoPrazo,
  planoAteAProva,
  provaSocial,
  rotuloDoBotao,
  tituloDaOferta,
  tituloDoCaminho,
} from "../../lib/momento"
import { Card, Carregando, Erro } from "../ui"
import { FormularioPix } from "../Acesso"
import BotaoCartao from "../Acesso/BotaoCartao"
import Pix from "../Acesso/Pix"
import RetornoDoCartao from "../Acesso/RetornoDoCartao"
import { QuestaoRevisadaCard } from "./QuestaoRevisada"

/*
 * A narrativa de quem fez o diagnóstico grátis, do ponto de partida até o
 * pedido: caminho → prova do mecanismo → continuidade → oferta → objeções →
 * chamada final. Vende preparação, não estoque de questão — a pessoa acabou de
 * descobrir onde está, e o que se oferece é o percurso daqui até "pronto".
 *
 * Nada aqui inventa número: preço e prazo vêm de `produtos`, a nota mínima e a
 * sequência de `prontidao`, as unidades da própria tentativa.
 */

// Lista curta, de propósito: acima disto vira segundo relatório, e o relatório
// inteiro já está no "diagnóstico completo".
const MAXIMO_DE_ASSUNTOS = 5

const POR_EXTENSO: Record<number, string> = { 1: "uma", 2: "duas", 3: "três" }

/**
 * A oferta empilhada da preparação completa, em três blocos — o que você
 * recebe, como você sabe que está pronto, o risco é nosso. Fórmula de
 * lançamento: promessa, bônus que respondem às objeções, risco do nosso lado.
 *
 * Tudo aqui vem do payload: prazo, extensão, guias, plano e flashcards são
 * dado do produto, e o que o produto não tem não aparece. Vender o que não
 * está pronto é a primeira coisa que um candidato de grupo de WhatsApp aponta.
 */
export type BlocoDaOferta = { titulo: string; itens: [string, string][] }

export function ofertaEmpilhada(produto: Produto, resultado: Resultado | null): BlocoDaOferta[] {
  const recebe: [string, string][] = []
  if (produto.habilitacoes.length > 1) {
    recebe.push([
      "Arrais-Amador e Motonauta no mesmo acesso",
      "Você faz uma prova só, a de Arrais: a moto aquática entra por agregação, com o treinamento. O simulado da NORMAM-212 vem junto.",
    ])
  }
  recebe.push(
    ["Simulados no formato da prova", "O mesmo número de questões, o mesmo tempo e a mesma nota de corte do exame da Capitania — quantos quiser."],
    ["Diagnóstico por unidade e questões comentadas", "A cada simulado, onde você perde ponto; em cada questão, a resposta certa, o comentário e a norma por trás."]
  )
  if (produto.plano_de_estudo) {
    recebe.push([
      "Plano de estudo até a sua prova",
      "Você diz a data; o painel monta o dia a dia — o que estudar hoje, quando fazer o simulado, o que revisar na véspera — e manda a tarefa por e-mail.",
    ])
  }
  if (produto.flashcards) {
    recebe.push([
      `${produto.flashcards} números que caem`,
      "Distâncias, prazos, idades e validades das normas, em cartões para revisar no celular, com o item de cada um.",
    ])
  }
  if (produto.materiais.length > 0) {
    recebe.push([`${produto.materiais.length} guias em PDF`, listar(produto.materiais) + "."])
  }
  recebe.push(["Tira-dúvidas em 24 h", "Não entendeu uma questão? Pergunte pelo suporte."])

  const pronto: [string, string][] = [
    [
      "Critério de pronto, por escrito",
      resultado ? criterioDePronto(resultado) : "Pronto é passar duas vezes seguidas, no formato da prova, com 7,0 ou mais.",
    ],
  ]

  const risco: [string, string][] = [
    produto.meses_de_extensao
      ? [
          "Acesso até a sua aprovação",
          `${prazo(produto.meses_de_acesso)} de acesso. Reprovou dentro do prazo? Mais ${produto.meses_de_extensao} meses sem custo, quantas vezes for preciso — é só pedir pelo suporte com a data da prova.`,
        ]
      : [`${prazo(produto.meses_de_acesso)} de acesso`, "Pagamento único. Sem mensalidade, sem renovação automática."],
    ["7 dias de garantia incondicional", "Desista em até sete dias e receba o reembolso integral, sem justificar."],
  ]

  return [
    { titulo: "O que você recebe", itens: recebe },
    { titulo: "Como você sabe que está pronto", itens: pronto },
    { titulo: "O risco é nosso", itens: risco },
  ]
}

/** A promessa da oferta: o critério e o risco, nunca a aprovação. */
export function promessaDaOferta(produto: Produto): string {
  return produto.meses_de_extensao
    ? "Marque a prova de Arrais sabendo que vai passar — e, se reprovar, a preparação continua por nossa conta."
    : "Marque a prova sabendo que vai passar."
}

/** "Pronto é passar duas vezes seguidas com 7,0 ou mais." — regra e nota vêm do servidor. */
export function criterioDePronto(resultado: Resultado): string {
  const { seguidas, nota_minima } = resultado.prontidao
  const vezes = POR_EXTENSO[seguidas] ?? String(seguidas)
  return `Pronto é passar ${vezes} vezes seguidas com ${formatarNota(nota_minima)} ou mais.`
}

function rotuloDePreco(produto: Produto): string {
  return produto.renovacao ? "renovação" : "pagamento único"
}

/**
 * "pagamento único, 1 ano. Sem assinatura. Ou 3× de R$ 32,33 sem juros." —
 * a alternativa de pagamento vem DEPOIS do preço cheio e do prazo, e só com
 * o cartão ligado no servidor (`produto.parcelas`). Parcela não é mensalidade.
 */
function condicoes(produto: Produto): string {
  const parcela = parcelado(produto.preco_centavos, produto.parcelas)
  return `${rotuloDePreco(produto)}, ${prazo(produto.meses_de_acesso)}. Sem assinatura.${parcela ? ` ${parcela[0].toUpperCase()}${parcela.slice(1)}.` : ""}`
}

/** O que o botão diz: o que o clique libera, e quanto custa — "quanto é?" não pode nascer no clique. */
function rotuloDoCta(resultado: Resultado, produto: Produto | null): string {
  const base = rotuloDoBotao(resultado.momento)
  return produto ? `${base} · ${reais(produto.preco_centavos)}` : base
}

/** Cabeçalho de seção no padrão do painel: marca-d'água em mono dourado, título em navy. */
function Secao({
  marca,
  titulo,
  children,
  className = "",
}: {
  marca: string
  titulo: string
  children: ReactNode
  className?: string
}) {
  return (
    <section className={`revela ${className}`}>
      <p className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-gold-600">{marca}</p>
      <h2 className="mt-1 text-lg font-bold leading-snug text-navy-900">{titulo}</h2>
      <div className="mt-4">{children}</div>
    </section>
  )
}

/** Em qual dos três botões da página o checkout está aberto. */
export type LocalDoCheckout = "resumida" | "oferta" | "final"

/** O que a pessoa escolheu pagar: a carteira desta prova, ou as duas de uma vez. */
export type Escolha = "unidade" | "pacote"

export interface CheckoutInline {
  aberto: LocalDoCheckout | null
  escolha: Escolha
  /** O produto escolhido — é ele que o formulário cobra. */
  produto: Produto | null
  unidade: Produto | null
  pacote: Produto | null
  compra: Compra | null
  erro: string | null
  abrir: (local: LocalDoCheckout, escolha?: Escolha) => void
  fechar: () => void
  registrar: (compra: Compra) => void
  falhar: (mensagem: string) => void
  liberar: () => void
}

/**
 * O Pix abre na própria página de resultado, no lugar do botão clicado. Trocar
 * de tela entre a decisão e o QR code é onde a pessoa repensa — e o checkout
 * em `/acesso` era exatamente essa troca.
 *
 * Os marcos do funil vão junto com o checkout, com os mesmos nomes e
 * parâmetros de sempre: `view_item` nasce da mesma chamada que o `/acesso` faz
 * (`/produtos?tela=acesso`), uma vez por página; `clique_pagar` é o clique
 * neste botão — com o QR estático o Pix nasce logo em seguida, sem CPF; no
 * caminho com CPF o clique revela o campo e `cpf_digitado` sai do próprio
 * `FormularioPix` quando ele fica completo; `begin_checkout` continua no
 * servidor, no `POST /compras`. Se o servidor não estiver oferecendo Pix, a
 * pessoa vai para o `/acesso` ver o que há — ele é quem decide.
 *
 * Dois produtos podem morar aqui: o da prova desta página e, no resultado de
 * Motonauta, a preparação completa do Arrais como degrau acima (no de Arrais
 * ela JÁ É o produto, e não há segundo). Um checkout aberto por vez;
 * `escolha` diz qual dos dois o formulário cobra.
 */
export function useCheckoutInline(
  slug: string,
  unidadeDaPagina: Produto | null,
  pacoteDaPagina: Produto | null,
  aoLiberar: () => void
): CheckoutInline {
  const [aberto, setAberto] = useState<LocalDoCheckout | null>(null)
  const [escolha, setEscolha] = useState<Escolha>("unidade")
  const [unidade, setUnidade] = useState<Produto | null>(null)
  const [pacote, setPacote] = useState<Produto | null>(null)
  const [gateways, setGateways] = useState<GatewayDePagamento[] | null>(null)
  const [compra, setCompra] = useState<Compra | null>(null)
  const [erro, setErro] = useState<string | null>(null)
  // "Pagar de outro jeito" num cartão em aberto: a pessoa quer o Pix, e o
  // cartão pendente não pode voltar sozinho no clique seguinte — senão ela
  // nunca chega ao QR. O Pix pendente, ao contrário, volta sempre (é o mesmo
  // QR, e gerar outro é o defeito que `compra_pendente` existe para evitar).
  const [cartaoDescartado, setCartaoDescartado] = useState(false)
  const navegar = useNavigate()

  const unidadeAtual = unidade ?? unidadeDaPagina
  const pacoteAtual = pacote ?? pacoteDaPagina

  // Pix gerado numa visita anterior e ainda no prazo: mostra ele de novo, no
  // primeiro botão da página, em vez de deixar a pessoa gerar outro. É o caso
  // de quem foi pagar no app do banco e voltou com a página recarregada. O do
  // pacote tem prioridade: é o maior dos dois.
  const pendente = pacoteDaPagina?.compra_pendente ?? unidadeDaPagina?.compra_pendente ?? null
  const pendenteDe: Escolha = pacoteDaPagina?.compra_pendente ? "pacote" : "unidade"
  useEffect(() => {
    if (pendente && !compra && aberto === null) {
      setCompra(pendente)
      setEscolha(pendenteDe)
      setAberto("resumida")
    }
    // Só na chegada do produto — depois disso quem manda é o clique.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendente])

  function abrir(local: LocalDoCheckout, qual: Escolha = "unidade") {
    setAberto(local)
    setEscolha(qual)
    setErro(null)
    // Fechou o QR e clicou de novo no mesmo produto: volta o mesmo Pix, não um
    // formulário novo. O cartão descartado fica descartado.
    const retomavel = pendente && !(pendente.gateway === "mercado_pago" && cartaoDescartado)
    if (retomavel && !compra && qual === pendenteDe) setCompra(pendente)
    // Dispara e não espera — medir nunca pode segurar o formulário.
    const alvo = qual === "pacote" ? (pacoteAtual?.slug ?? slug) : slug
    api.post<unknown>("/eventos", { nome: "clique_pagar", habilitacao: alvo, gateway: "asaas" }).catch(() => {})
    if (gateways !== null) return
    api
      .get<{ produtos: Produto[]; gateways: GatewayDePagamento[] }>(
        `/produtos?tela=acesso&habilitacao=${encodeURIComponent(slug)}`
      )
      .then((r) => {
        setGateways(r.gateways)
        setUnidade(r.produtos.find((x) => x.slug === slug) ?? null)
        setPacote(r.produtos.find((x) => x.pacote && x.slug !== slug) ?? null)
        if (!r.gateways.includes("asaas")) navegar(`/acesso?habilitacao=${slug}`)
      })
      .catch(() => navegar(`/acesso?habilitacao=${slug}`))
  }

  return {
    aberto,
    escolha,
    produto: escolha === "pacote" ? pacoteAtual : unidadeAtual,
    unidade: unidadeAtual,
    pacote: pacoteAtual,
    compra,
    erro,
    abrir,
    fechar: () => {
      if (compra?.gateway === "mercado_pago") setCartaoDescartado(true)
      setAberto(null)
      setCompra(null)
      setErro(null)
    },
    registrar: (c) => {
      setErro(null)
      setCompra(c)
    },
    falhar: setErro,
    liberar: aoLiberar,
  }
}

/**
 * O botão da jornada — e, depois do clique, o checkout no mesmo lugar: o campo
 * de CPF entra ao lado, o botão vira "Gerar Pix", e nada mais no card se move.
 * O QR code é a única coisa que ocupa espaço novo, e a essa altura a pessoa já
 * decidiu. Um só aberto por vez: clicar em outro botão traz o campo para lá.
 */
export function CtaPreparacao({
  local,
  checkout,
  rotulo,
  escolha = "unidade",
  className = "",
}: {
  local: LocalDoCheckout
  checkout: CheckoutInline
  /** Ver rotuloDoCta: muda com o momento da prova e traz o preço. */
  rotulo: string
  /** Qual produto este botão cobra. */
  escolha?: Escolha
  className?: string
}) {
  if (checkout.aberto !== local || checkout.escolha !== escolha) {
    return (
      <button
        type="button"
        onClick={() => checkout.abrir(local, escolha)}
        className={`inline-flex w-full items-center justify-center rounded-xl bg-sucesso-700 px-6 py-3.5 text-sm font-bold text-white transition hover:bg-sucesso-800 sm:w-auto ${className}`}
      >
        {rotulo}
      </button>
    )
  }

  if (checkout.compra) {
    return (
      <div className={`w-full max-w-md text-left ${className}`}>
        {checkout.compra.gateway === "mercado_pago" ? (
          <RetornoDoCartao
            compra={checkout.compra}
            estado={null}
            aoLiberar={checkout.liberar}
            aoPagarDeOutroJeito={checkout.fechar}
          />
        ) : (
          <Pix compra={checkout.compra} aoLiberar={checkout.liberar} aoVoltar={checkout.fechar} />
        )}
      </div>
    )
  }

  return (
    <div className={`w-full text-left sm:w-auto ${className}`}>
      {checkout.erro && <Erro mensagem={checkout.erro} />}
      {checkout.produto ? (
        <>
          <FormularioPix
            layout="linha"
            produto={checkout.produto}
            aoCriar={checkout.registrar}
            aoFalhar={checkout.falhar}
          />
          {/* O formulário cuida de si (gera o Pix, ou pede o CPF com o porquê
              acima do campo). Aqui fica só o que acontece depois. Sem "agora
              não": quem clicou em pagar não precisa de uma saída à mão. */}
          <p className="mt-2 max-w-[19rem] text-xs text-slate-500">O QR code aparece aqui.</p>
          {/* O cartão, abaixo do Pix e menor: é a alternativa, não o caminho.
              Some sozinho quando o servidor não o oferece. */}
          <BotaoCartao produto={checkout.produto} aoFalhar={checkout.falhar} className="mt-3" />
        </>
      ) : (
        <Carregando texto="Abrindo o Pix…" />
      )}
    </div>
  )
}

/**
 * A ponte que faltava entre o problema e o preço: quatro passos, o primeiro já
 * feito. É o "estou aqui → faço isso → chego ali" — escaneável, sem parágrafo.
 */
export function Caminho({ resultado }: { resultado: Resultado }) {
  const passos: [string, string][] = [
    ["Diagnosticar", "Descobrir exatamente onde você está errando."],
    ["Entender", "Ver a resposta certa, o comentário e a norma por trás de cada questão."],
    ["Treinar", "Fazer simulados no formato da prova e acompanhar sua evolução."],
    ["Estar pronto", `Continuar até passar com margem. Aqui, ${criterioDePronto(resultado).replace(/^Pronto/, "pronto")}`],
  ]

  return (
    <Secao marca="O caminho" titulo={tituloDoCaminho(resultado.momento)}>
      <ol className="grid gap-5 sm:grid-cols-4 sm:gap-4">
        {passos.map(([titulo, texto], i) => {
          const feito = i === 0
          const proximo = i === 1
          return (
            <li
              key={titulo}
              className="relative sm:after:absolute sm:after:left-11 sm:after:top-4 sm:after:h-px sm:after:w-[calc(100%-2.75rem)] sm:after:bg-gold-500/40 sm:last:after:hidden"
            >
              <div className="flex items-center gap-3">
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border font-mono text-xs font-semibold ${
                    feito
                      ? "border-gold-500 bg-gold-500 text-navy-900"
                      : "border-gold-500/60 bg-white text-gold-600"
                  }`}
                  aria-label={feito ? "Passo concluído" : undefined}
                >
                  {feito ? "✓" : String(i + 1).padStart(2, "0")}
                </span>
                {feito && <span className="text-xs font-semibold text-emerald-700 sm:hidden">Feito</span>}
                {proximo && <span className="text-xs font-semibold text-gold-600 sm:hidden">Próximo passo</span>}
              </div>
              <h3 className="mt-3 text-sm font-semibold text-navy-900">
                {titulo}
                {feito && <span className="ml-2 hidden text-xs font-semibold text-emerald-700 sm:inline">Feito</span>}
                {proximo && <span className="ml-2 hidden text-xs font-semibold text-gold-600 sm:inline">Próximo</span>}
              </h3>
              <p className="mt-1 text-sm leading-relaxed text-slate-600">{texto}</p>
            </li>
          )
        })}
      </ol>
    </Secao>
  )
}

/**
 * Preço e botão logo depois do caminho, para quem já entendeu e não precisa
 * atravessar exemplo e mapa até achar o preço. A oferta inteira, com os
 * benefícios e as objeções, continua mais abaixo — esta é a versão de uma linha.
 */
export function OfertaResumida({
  resultado,
  produto,
  pacote,
  checkout,
}: {
  resultado: Resultado
  produto: Produto | null
  pacote: Produto | null
  checkout: CheckoutInline
}) {
  // O cabeçalho fica onde está enquanto o CPF entra ao lado do botão; só o QR
  // code, que é grande, empilha o card.
  const comQr = checkout.aberto === "resumida" && checkout.escolha === "unidade" && checkout.compra !== null
  // Quem tem prova esta semana ou este mês lê os próximos dias, não "seis
  // meses"; quem passou no diagnóstico sem ter marcado lê "confirme antes de
  // marcar" em vez de "treine até passar" — já passou, e sairia daqui pronto.
  const plano = planoAteAProva(resultado.momento, resultado)
  const manchete = (resultado.aprovada && mancheteDoAprovado(resultado.momento)) || mancheteDaOferta(resultado.momento)
  return (
    <Card className="revela border-gold-500">
      <div className={comQr ? "" : "sm:flex sm:items-center sm:justify-between sm:gap-6"}>
        <div>
          <p className="font-semibold text-navy-900">{manchete}</p>
          {plano ? (
            <>
              <ul className="mt-2 space-y-1 text-sm text-slate-600">
                {plano.map(([quando, oQue]) => (
                  <li key={quando}>
                    <strong className="font-semibold text-navy-900">{quando}:</strong> {oQue}
                  </li>
                ))}
              </ul>
              {produto && (
                <p className="mt-2 text-sm text-slate-600">
                  <span className="font-semibold text-navy-900">{reais(produto.preco_centavos)}</span>, {condicoes(produto)}
                </p>
              )}
            </>
          ) : (
            <p className="mt-1 text-sm text-slate-600">
              Preparação completa para {resultado.habilitacao}
              {produto ? (
                <>
                  {" "}
                  — <span className="font-semibold text-navy-900">{reais(produto.preco_centavos)}</span>, {condicoes(produto)}
                </>
              ) : (
                "."
              )}
            </p>
          )}
          {produto && <Relampago produto={produto} />}
        </div>
        <CtaPreparacao
          local="resumida"
          checkout={checkout}
          rotulo={rotuloDoCta(resultado, produto)}
          className={comQr ? "mt-4" : "mt-4 shrink-0 sm:mt-0"}
        />
      </div>

      {/* O degrau acima, só no resultado de Motonauta: quem vai pilotar barco
          também faz UMA prova, a de Arrais — e a preparação completa já inclui
          o Motonauta. É a regra da norma (NORMAM-211 5.4.2 d), não upsell. */}
      {pacote && (
        <div className="mt-5 border-t border-navy-50 pt-4">
          <p className="text-sm text-navy-900">
            <strong className="font-semibold">Vai pilotar barco também?</strong> Então você faz uma prova só, a de
            Arrais — e a preparação completa do Arrais já inclui o Motonauta:{" "}
            <span className="font-semibold">{reais(pacote.preco_centavos)}</span>, {rotuloDePreco(pacote)},{" "}
            {prazo(pacote.meses_de_acesso)}
            {pacote.meses_de_extensao ? " e acesso até a sua aprovação" : ""}.
          </p>
          <Relampago produto={pacote} />
          <CtaPreparacao
            local="resumida"
            escolha="pacote"
            checkout={checkout}
            rotulo={`Quero a preparação completa · ${reais(pacote.preco_centavos)}`}
            className="mt-3"
          />
        </div>
      )}
    </Card>
  )
}

/**
 * A oferta relâmpago, só enquanto vale: "de R$ 39,90 por R$ 19,90 · até
 * 15/10". O preço riscado é o praticado (`preco_depois_centavos`, que é o que
 * volta a valer depois), e o prazo é do servidor — é isso que torna o "de"
 * verdadeiro (CDC, art. 37). A página não inventa contagem regressiva.
 */
export function Relampago({ produto }: { produto: Produto }) {
  if (!produto.relampago_ate || produto.preco_depois_centavos === null) return null
  const ate = new Date(produto.relampago_ate).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit" })
  return (
    <p className="mt-2 text-xs font-semibold text-gold-700">
      <s className="font-normal text-slate-500">de {reais(produto.preco_depois_centavos)}</s> por{" "}
      {reais(produto.preco_centavos)} · promoção até {ate}
    </p>
  )
}

/**
 * Prova do mecanismo: UMA questão, de preferência errada, com o gabarito
 * inteiro. Antes a seção se chamava "amostra do gabarito" — nome de feature. O
 * que se demonstra é o erro virando aprendizado, que é o produto.
 */
export function ProvaDoMecanismo({ demo }: { demo: QuestaoRevisada }) {
  const errou = demo.correta === false
  return (
    <Secao
      marca="Como funciona"
      titulo={errou ? "Veja como um erro vira aprendizado" : "Veja como cada questão vem comentada"}
    >
      <QuestaoRevisadaCard q={demo} />
      <p className="mt-3 text-sm text-navy-800">
        É assim que você vai estudar cada {errou ? "erro" : "questão"}: entendendo a resposta e a regra por
        trás dela — não decorando a letra.
      </p>
    </Secao>
  )
}

/**
 * Continuidade personalizada: as unidades que a pessoa acabou de errar viram o
 * mapa do que vem a seguir. Não é pacote genérico — o sistema acabou de
 * apontar o que ELA precisa estudar.
 *
 * As regras do grátis seguem intactas: o que a confirmação de e-mail libera
 * (o restante do gabarito desta tentativa) continua grátis e é pedido aqui,
 * pelo mesmo componente de antes; o que se paga é a preparação inteira.
 */
export function Continuidade({
  resultado,
  fracas,
  restantes,
}: {
  resultado: Resultado
  fracas: ResultadoUnidade[]
  restantes: QuestaoRevisada[]
}) {
  return (
    <Secao marca="Por onde começar" titulo="Comece por estes assuntos">
      {fracas.length > 0 ? (
        <Card>
          <ul className="divide-y divide-navy-50">
            {fracas.slice(0, MAXIMO_DE_ASSUNTOS).map((u) => (
              <li key={u.codigo} className="flex items-center gap-3 py-2.5 text-sm">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-gold-500" aria-hidden />
                <span className="text-navy-900">{u.unidade}</span>
                <span className="ml-auto font-mono text-xs text-slate-500">{u.codigo}</span>
              </li>
            ))}
          </ul>
          {fracas.length > MAXIMO_DE_ASSUNTOS && (
            <p className="mt-2 text-xs text-slate-500">
              Mais {fracas.length - MAXIMO_DE_ASSUNTOS} no diagnóstico completo, acima.
            </p>
          )}
          <p className="mt-3 text-sm text-slate-600">
            Cada um tem questões comentadas na preparação e entra nos simulados no formato da prova.
            Estude o primeiro, faça um simulado, veja o que mudou.
          </p>
        </Card>
      ) : (
        <Card>
          <p className="text-sm text-navy-800">
            Você fechou todas as unidades deste diagnóstico. O próximo passo é confirmar isso no formato
            da prova, com mais questões e o tempo contado.
          </p>
        </Card>
      )}

      {restantes.length > 0 && (
        <details className="group mt-3">
          <summary className="flex list-none items-center gap-2 text-sm font-semibold text-navy-800 [&::-webkit-details-marker]:hidden">
            <Chevron />
            Ver {restantes.length === 1 ? "a outra questão comentada" : `as outras ${restantes.length} questões comentadas`}
          </summary>
          <div className="mt-3 space-y-4">
            {restantes.map((q) => (
              <QuestaoRevisadaCard key={q.questao_id} q={q} />
            ))}
          </div>
        </details>
      )}

      {!resultado.gabarito_liberado && <GabaritoRetido resultado={resultado} />}
    </Secao>
  )
}

/**
 * A oferta: preparação até saber que está pronto, não "acesso a simulados".
 * Benefícios escaneáveis, preço à vista com a forma (uma vez) ao lado, e o
 * direito de desistir logo abaixo do botão — direito que ninguém conhece não
 * tira o medo de ninguém.
 */
export function Oferta({
  resultado,
  produto,
  pacote,
  checkout,
}: {
  resultado: Resultado
  produto: Produto | null
  pacote: Produto | null
  checkout: CheckoutInline
}) {
  // A preparação completa vem empilhada em três blocos; o avulso de
  // Motonauta, com a lista curta de sempre.
  const blocos: BlocoDaOferta[] = produto?.pacote
    ? ofertaEmpilhada(produto, resultado)
    : [
        {
          titulo: "O que você recebe",
          itens: [
            ["Diagnóstico por unidade", "A cada simulado, saiba onde concentrar o estudo."],
            ["Questões comentadas", "A resposta certa, o comentário e a norma citada quando houver (NORMAM, LESTA, RIPEAM)."],
            ["Simulados no formato da prova", "O mesmo número de questões, o mesmo tempo e a mesma nota de corte do exame da Capitania."],
            ["Histórico de evolução", "Acompanhe sua nota subindo ao longo da preparação."],
            beneficioDoPrazo(resultado.momento, produto?.meses_de_acesso ?? null),
          ],
        },
      ]

  // Contado no banco, arredondado para baixo: um site de quinze minutos pedindo
  // Pix precisa mostrar que não está vazio.
  const social = provaSocial(resultado.candidatos_com_diagnostico)

  return (
    <Secao marca="A preparação" titulo={tituloDaOferta(resultado.momento)}>
      <Card className="border-gold-500 p-6 sm:p-7">
        <p className="text-sm font-semibold text-navy-900">
          {produto?.pacote ? promessaDaOferta(produto) : `Preparação completa para ${resultado.habilitacao}.`}
        </p>
        {social && <p className="mt-1 text-sm text-slate-600">{social}</p>}

        {blocos.map((bloco) => (
          <div key={bloco.titulo} className="mt-5">
            {blocos.length > 1 && (
              <p className="font-mono text-[0.65rem] uppercase tracking-[0.22em] text-gold-600">{bloco.titulo}</p>
            )}
            <ul className="mt-2 space-y-3">
              {bloco.itens.map(([titulo, texto]) => (
                <li key={titulo} className="flex gap-3 text-sm">
                  <span className="mt-0.5 font-bold text-gold-600" aria-hidden>
                    ✓
                  </span>
                  <span>
                    <strong className="font-semibold text-navy-900">{titulo}</strong>
                    <span className="block text-slate-600">{texto}</span>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="mt-6 border-t border-navy-50 pt-5">
          {produto && (
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <p className="text-3xl font-extrabold tracking-tight text-navy-900">{reais(produto.preco_centavos)}</p>
              <p className="text-sm text-slate-600">{rotuloDePreco(produto)}</p>
            </div>
          )}
          {produto && parcelado(produto.preco_centavos, produto.parcelas) && (
            <p className="mt-0.5 text-xs text-slate-500">{parcelado(produto.preco_centavos, produto.parcelas)}</p>
          )}
          <p className="mt-1 text-sm font-semibold text-navy-900">Sem mensalidade. Sem renovação automática.</p>

          {produto && <Relampago produto={produto} />}

          <CtaPreparacao local="oferta" checkout={checkout} rotulo={rotuloDoCta(resultado, produto)} className="mt-5" />

          <p className="mt-3 text-xs text-slate-500">
            Sete dias para desistir, com reembolso integral e sem justificar.
          </p>
        </div>

        {/* No resultado de Motonauta: o degrau acima. Quem vai pilotar barco
            faz uma prova só, a de Arrais — e ela já inclui o Motonauta. */}
        {pacote && (
          <div className="mt-6 rounded-xl border border-navy-100 bg-navy-50/60 p-5">
            <p className="text-sm font-semibold text-navy-900">Vai pilotar barco também? {pacote.nome}</p>
            <p className="mt-1 text-sm text-slate-600">
              Você faz uma prova só, a de Arrais — a moto aquática entra por agregação, com o treinamento. A
              preparação completa já inclui o simulado de Motonauta.
            </p>
            <ul className="mt-3 space-y-2 text-sm text-slate-700">
              {ofertaEmpilhada(pacote, resultado)
                .flatMap((bloco) => bloco.itens)
                .map(([titulo]) => (
                  <li key={titulo} className="flex gap-3">
                    <span className="mt-0.5 font-bold text-gold-600" aria-hidden>
                      ✓
                    </span>
                    <span>{titulo}</span>
                  </li>
                ))}
            </ul>
            <div className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <p className="text-2xl font-extrabold tracking-tight text-navy-900">{reais(pacote.preco_centavos)}</p>
              <p className="text-sm text-slate-600">{rotuloDePreco(pacote)} · inclui o Motonauta</p>
            </div>
            <Relampago produto={pacote} />
            <CtaPreparacao
              local="oferta"
              escolha="pacote"
              checkout={checkout}
              rotulo={`Quero a preparação completa · ${reais(pacote.preco_centavos)}`}
              className="mt-4"
            />
          </div>
        )}
      </Card>
    </Secao>
  )
}

/**
 * As objeções de quem chegou até aqui, respondidas curto. A primeira é a mais
 * importante: candidato real estuda por apostila, curso e vídeo, e ninguém vai
 * abandonar o próprio material — o produto é complemento, não substituto.
 */
export function Objecoes({ resultado, produto }: { resultado: Resultado; produto: Produto | null }) {
  const preco = produto ? reais(produto.preco_centavos) : "uma vez"
  const meses = produto ? prazo(produto.meses_de_acesso) : "o período contratado"

  const doPrazo = objecaoDoPrazo(resultado.momento)
  const doCusto = objecaoDoCusto(resultado.momento)
  const itens: [string, ReactNode][] = [
    ...(doPrazo ? [doPrazo] : []),
    ...(doCusto ? [doCusto] : []),
    [
      "“Já estudo por apostila, curso ou vídeo.”",
      "Ótimo — continue. O CarteiraNáutica não substitui seu material: ele mostra onde concentrar o estudo e verifica se o que você estudou foi suficiente para passar.",
    ],
    [
      "“Não seria melhor procurar questões grátis?”",
      "Questão solta diz se você acertou. Aqui cada erro vem com a resposta, o comentário e a norma por trás, e o simulado segue a matriz de unidades do programa oficial da Marinha, com o número de questões, o tempo e a nota de corte da prova real. Treinar fora disso mede a coisa errada.",
    ],
    [
      "“Não quero outra assinatura.”",
      `Não é assinatura. Você paga ${preco} uma única vez e usa durante ${meses}. Nada renova sozinho.`,
    ],
    [
      "“E se eu comprar e não gostar?”",
      "Você tem sete dias para desistir e pedir o reembolso integral, sem justificar.",
    ],
    [
      "“Como vou saber quando estou pronto?”",
      `Pelas suas notas nos simulados no formato da prova. ${criterioDePronto(resultado)} Não é garantia de aprovação — é evidência de que a preparação funcionou.`,
    ],
  ]

  return (
    <Secao marca="Dúvidas" titulo="Talvez você esteja pensando…">
      <Card className="divide-y divide-navy-50 p-0">
        {itens.map(([pergunta, resposta]) => (
          <details key={pergunta} className="group px-5">
            <summary className="flex list-none items-start gap-3 py-4 text-sm font-semibold text-navy-900 [&::-webkit-details-marker]:hidden">
              <Chevron className="mt-1" />
              {pergunta}
            </summary>
            <p className="pb-4 pl-7 text-sm leading-relaxed text-slate-700">{resposta}</p>
          </details>
        ))}
      </Card>
    </Secao>
  )
}

/** Fecha a narrativa: ponto de partida descoberto, falta o caminho. */
export function ChamadaFinal({
  resultado,
  produto,
  checkout,
}: {
  resultado: Resultado
  produto: Produto | null
  checkout: CheckoutInline
}) {
  const itens = [
    produto && `${prazo(produto.meses_de_acesso)} de acesso`,
    "sem assinatura",
    "sem renovação automática",
    "7 dias para desistir",
  ].filter((x): x is string => Boolean(x))
  // A promessa da landing mora em lib/momento: é a mesma jornada, e trocar a
  // frase entre uma tela e outra soaria como outro produto — menos para quem
  // já marcou, a quem "marque a prova" contradiz o que acabou de responder.
  const { titulo, texto } = chamadaFinal(resultado.momento)
  const social = provaSocial(resultado.candidatos_com_diagnostico)

  return (
    <section className="revela pb-8 text-center">
      <h2 className="text-lg font-bold leading-snug text-navy-900">
        {titulo[0]}
        <br className="hidden sm:block" /> {titulo[1]}
      </h2>
      <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600">{texto}</p>
      {social && <p className="mx-auto mt-2 max-w-xl text-sm text-slate-600">{social}</p>}
      {produto && (
        <p className="mt-4 text-sm font-semibold text-navy-900">
          {reais(produto.preco_centavos)} — {rotuloDePreco(produto)}
          {parcelado(produto.preco_centavos, produto.parcelas) && (
            <span className="block text-xs font-normal text-slate-500">{parcelado(produto.preco_centavos, produto.parcelas)}</span>
          )}
        </p>
      )}
      <div className="mt-4 flex justify-center">
        <CtaPreparacao local="final" checkout={checkout} rotulo={rotuloDoCta(resultado, produto)} />
      </div>
      <p className="mt-3 text-xs text-slate-500">{itens.join(" • ")}</p>
      <p className="mt-6 text-sm">
        <Link to="/historico" className="font-semibold text-navy-700 underline">
          Ver histórico
        </Link>
      </p>
    </section>
  )
}

/**
 * O pedido de confirmação mora no resultado, e não na entrada, de propósito: a
 * pessoa já respondeu o diagnóstico e acabou de ver onde perdeu ponto, então
 * tem motivo para abrir o e-mail. E vem DEPOIS de uma questão comentada — zero
 * de dezoito confirmavam quando o pedido era abstrato. O que fica atrás é
 * contado, e a copy diz exatamente isso: nada de "acesso negado".
 */
function GabaritoRetido({ resultado }: { resultado: Resultado }) {
  const { amostra, retidas, email } = resultado
  const [reenvio, setReenvio] = useState<"parado" | "enviando" | "enviado" | "erro">("parado")

  async function reenviar() {
    setReenvio("enviando")
    try {
      await api.post<{ email: string }>("/confirmacao", {})
      setReenvio("enviado")
    } catch {
      setReenvio("erro")
    }
  }

  const titulo =
    amostra.length === 0
      ? "Seu gabarito comentado está pronto"
      : retidas === 1
        ? "Mais 1 questão comentada deste diagnóstico está pronta"
        : `Mais ${retidas} questões comentadas deste diagnóstico estão prontas`

  return (
    <Card className="mt-3 border-navy-200 bg-navy-50/60">
      <h3 className="font-bold text-navy-900">{titulo}</h3>
      {/* "sem pagar nada" não é enfeite: este card fica perto da oferta paga, e
          sem essa palavra parte das pessoas lê a confirmação como parte do
          paywall e desiste achando que precisa comprar. O que a confirmação
          libera é grátis; o que se paga é outra coisa. */}
      <p className="mt-1 text-sm text-navy-800">
        Confirme seu e-mail — <strong>sem pagar nada</strong> — para ver a resposta certa, o comentário e
        a base legal de cada uma.
        {email && (
          <>
            {" "}
            Mandamos o link para <strong>{email}</strong>.
          </>
        )}
      </p>
      <p className="mt-3 text-sm">
        {reenvio === "enviado" ? (
          <span className="text-emerald-700">
            Reenviado{email && <> para <strong>{email}</strong></>}. Vale olhar o spam.
          </span>
        ) : reenvio === "erro" ? (
          <span>
            Não deu para reenviar agora.{" "}
            <a href="/confirmar/new" className="font-semibold text-navy-800 underline">
              Tentar pelo formulário
            </a>
          </span>
        ) : (
          <button
            type="button"
            onClick={reenviar}
            disabled={reenvio === "enviando"}
            className="font-semibold text-navy-800 underline disabled:opacity-60"
          >
            {reenvio === "enviando" ? "Reenviando…" : "Não recebeu? Reenviar o e-mail"}
          </button>
        )}
      </p>
    </Card>
  )
}

function Chevron({ className = "" }: { className?: string }) {
  return (
    <svg
      className={`h-4 w-4 shrink-0 text-gold-600 transition-transform group-open:rotate-90 ${className}`}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M7 5l6 5-6 5" />
    </svg>
  )
}

